import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import LoadingSpinner from "../components/LoadingSpinner";
import MessageCard from "../components/MessageCard";
import toast from "react-hot-toast";
import { AlertTriangle } from "lucide-react";

const MISSING_ATTR_COPY = {
  missing_mail: {
    title: "Sign-in failed: missing mailbox",
    body: "Your Microsoft account is missing the mail attribute, which is required to sign in. Contact your IT support to set the user.mail attribute on your Entra ID account, then try again.",
  },
  missing_upn: {
    title: "Sign-in failed: missing user principal name",
    body: "Your Microsoft account is missing its user principal name (the userPrincipalName attribute), which is required to sign in. Contact your IT support to fix the directory entry, then try again.",
  },
} as const;

type MissingReason = keyof typeof MISSING_ATTR_COPY;

export default function AuthCallbackPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [missing, setMissing] = useState<MissingReason | null>(null);

  useEffect(() => {
    // Prevent duplicate processing in React StrictMode
    if (hasProcessed.current) return;
    const handleOAuthCallback = async () => {
      hasProcessed.current = true;

      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get("success");
      const error = urlParams.get("error");
      const reason = urlParams.get("reason");

      // Explicit allowlist: any other reason value (typo, stale URL, etc.)
      // falls through to the generic "Invalid callback" / error path below.
      if (
        success === "false" &&
        (reason === "missing_mail" || reason === "missing_upn")
      ) {
        setMissing(reason);
        return;
      }

      if (error) {
        toast.error("Authentication failed: " + error, {
          duration: 6000, // Show error for 6 seconds
          position: "top-center",
        });
        // Redirect to login page after error
        setTimeout(() => {
          window.location.href = "/";
        }, 3000);
        return;
      }

      if (success === "true") {
        try {
          // Fetch the token from the backend
          const response = await fetch("/api/auth/token", {
            method: "GET",
            credentials: "include", // Include cookies
          });

          if (!response.ok) {
            throw new Error("Failed to retrieve authentication token");
          }

          const data = await response.json();

          if (!data.success || !data.data?.token) {
            throw new Error("Invalid response from authentication server");
          }

          const token = data.data.token;

          // Decode the JWT to get user info (simple base64 decode of payload)
          const tokenParts = token.split(".");
          if (tokenParts.length !== 3 || !tokenParts[1]) {
            throw new Error("Invalid token format");
          }

          const payload = JSON.parse(atob(tokenParts[1]));

          const authResponse = {
            token,
            user: {
              id: payload.user_id,
              email: payload.email,
              name: payload.name,
              businessArea: payload.business_area,
              division: payload.division,
              roleId: payload.role_id,
              role: payload.role,
              userType: payload.user_type,
              supplementaryRoles: payload.supplementary_roles ?? [],
            },
          };

          // Use existing login method
          login(authResponse);
          toast.success("Login successful! Welcome back.", {
            duration: 4000, // Show success for 4 seconds
            position: "top-center",
          });

          const redirectPath =
            localStorage.getItem("postLoginRedirect") || "/dashboard";
          localStorage.removeItem("postLoginRedirect");

          // Navigate client-side rather than reloading the page. WebKit flushes
          // localStorage asynchronously, so a full-page navigation in the same
          // tick as the login() write can discard the token before it lands.
          navigate(redirectPath, { replace: true });
        } catch (error) {
          console.error("Failed to authenticate:", error);
          toast.error("Authentication failed: " + (error as Error).message, {
            duration: 8000, // Show error for 8 seconds
            position: "top-center",
          });
          setTimeout(() => {
            window.location.href = "/";
          }, 3000);
        }
      } else {
        toast.error("Authentication failed: Invalid callback", {
          duration: 6000, // Show error for 6 seconds
          position: "top-center",
        });
        setTimeout(() => {
          window.location.href = "/";
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [login, navigate]);

  if (missing) {
    const copy = MISSING_ATTR_COPY[missing];
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="max-w-md w-full mx-4">
          <MessageCard
            icon={AlertTriangle}
            title={copy.title}
            tone="danger"
            action={
              <a href="/" className="btn btn-primary w-full">
                Return to login
              </a>
            }
          >
            {copy.body}
          </MessageCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">Completing authentication...</p>
      </div>
    </div>
  );
}
